import React, { useEffect } from 'react';
import { AlertTriangle, Package, X, CheckCircle2, ChevronRight, Copy, Info } from 'lucide-react';
import Apiurl from '../../Helpers/apiUrl';
import { formatCurrency, getCurrencySymbol } from '../../Helpers/currencyHelper';

export default function StockConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  products = [],
  totalCount = 0,
  mode = 'out_of_stock', // 'out_of_stock' | 'duplicate' | 'unique'
}) {
  const [activeBtn, setActiveBtn] = React.useState('confirm');

  useEffect(() => {
    if (isOpen) {
      setActiveBtn('confirm');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || products.length === 0) return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        setActiveBtn(prev => prev === 'confirm' ? 'cancel' : 'confirm');
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (activeBtn === 'confirm') onConfirm();
        else onClose();
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, products.length, onConfirm, onClose, activeBtn]);
  if (!isOpen || products.length === 0) return null;


  const inStockCount = totalCount - products.length;

  let headerGradient = 'linear-gradient(135deg,#fffbeb 0%,#fef3c7 100%)';
  let headerBorder = '#fde68a';
  let iconBg = '#f59e0b';
  let iconShadow = '0 4px 12px rgba(245,158,11,.35)';
  let headerTitleColor = 'text-amber-900';
  let headerSubtitleColor = 'text-amber-700';
  let closeBtnColor = 'text-amber-400 hover:text-amber-700 hover:bg-amber-100';
  let rowBg = '#fef9ee';
  let rowBorder = '#fde68a';
  let imgBorder = 'border-amber-100';
  let imgPlaceholderBg = 'bg-amber-100';
  let IconComponent = <AlertTriangle size={20} color="#fff" strokeWidth={2.5} />;
  let placeholderIconColor = '#d97706';
  let buttonGradient = 'linear-gradient(135deg,#f59e0b,#d97706)';
  let buttonShadow = '0 4px 14px rgba(245,158,11,.4)';
  let buttonRing = 'ring-amber-500';
  let headerTitle = `Out-of-Stock ${products.length === 1 ? 'Product' : 'Products'} Detected`;
  let headerSubtitle = products.length === 1
    ? '1 product below has zero stock. Add it anyway?'
    : `${products.length} products below have zero stock. Add them anyway?`;
  let confirmText = 'Add Anyway';

  if (mode === 'duplicate') {
    headerGradient = 'linear-gradient(135deg,#e0e7ff 0%,#c7d2fe 100%)';
    headerBorder = '#a5b4fc';
    iconBg = '#6366f1';
    iconShadow = '0 4px 12px rgba(99,102,241,.35)';
    headerTitleColor = 'text-indigo-900';
    headerSubtitleColor = 'text-indigo-700';
    closeBtnColor = 'text-indigo-400 hover:text-indigo-700 hover:bg-indigo-100';
    rowBg = '#f5f7ff';
    rowBorder = '#c7d2fe';
    imgBorder = 'border-indigo-100';
    imgPlaceholderBg = 'bg-indigo-100';
    IconComponent = <Copy size={20} color="#fff" strokeWidth={2.5} />;
    placeholderIconColor = '#4f46e5';
    buttonGradient = 'linear-gradient(135deg,#6366f1,#4f46e5)';
    buttonShadow = '0 4px 14px rgba(99,102,241,.4)';
    buttonRing = 'ring-indigo-500';
    headerTitle = 'Duplicate Product Detected';
    headerSubtitle = 'This product is already in your current selection. Add it again?';
    confirmText = 'Add Duplicate';
  } else if (mode === 'unique') {
    headerGradient = 'linear-gradient(135deg,#dcfce7 0%,#bbf7d0 100%)';
    headerBorder = '#86efac';
    iconBg = '#22c55e';
    iconShadow = '0 4px 12px rgba(34,197,94,.35)';
    headerTitleColor = 'text-emerald-900';
    headerSubtitleColor = 'text-emerald-700';
    closeBtnColor = 'text-emerald-400 hover:text-emerald-700 hover:bg-emerald-100';
    rowBg = '#f6fdf9';
    rowBorder = '#bbf7d0';
    imgBorder = 'border-emerald-100';
    imgPlaceholderBg = 'bg-emerald-100';
    IconComponent = <CheckCircle2 size={20} color="#fff" strokeWidth={2.5} />;
    placeholderIconColor = '#16a34a';
    buttonGradient = 'linear-gradient(135deg,#22c55e,#16a34a)';
    buttonShadow = '0 4px 14px rgba(34,197,94,.4)';
    buttonRing = 'ring-emerald-500';
    headerTitle = 'Confirm Product Addition';
    headerSubtitle = 'You are about to add this product to your selection. Proceed?';
    confirmText = 'Add Product';
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '85vh',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          border: `1.5px solid ${headerBorder}`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-start gap-3 px-5 pt-5 pb-4"
          style={{ background: headerGradient, borderBottom: `1px solid ${headerBorder}` }}
        >
          <div
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: iconBg, boxShadow: iconShadow }}
          >
            {IconComponent}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[15px] font-bold ${headerTitleColor} leading-tight`}>
              {headerTitle}
            </h2>
            <p className={`text-[12px] ${headerSubtitleColor} mt-0.5 leading-snug`}>
              {headerSubtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${closeBtnColor}`}
          >
            <X size={15} />
          </button>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {products.map((p, idx) => {
            const stockVal = Number(p.stockAvailable || p.stock || 0);
            const isZeroStock = stockVal <= 0;
            return (
              <div
                key={p.productCode || idx}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: rowBg, border: `1px solid ${rowBorder}` }}
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.productCode}
                    className={`w-9 h-9 rounded-lg object-cover flex-shrink-0 border ${imgBorder}`}
                    onError={(e) => {
                      const fallback = Apiurl.replace('/api/', '/img/no_image.jpeg');
                      if (e.target.src !== fallback) {
                        e.target.src = fallback;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${imgPlaceholderBg}`}>
                    <Package size={16} color={placeholderIconColor} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-extrabold text-slate-700 tracking-wide font-mono">
                      {p.productCode}
                    </span>
                    {p.brand && (
                      <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">
                        {p.brand}
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <p className="text-[11px] text-slate-500 truncate mt-0.5 leading-tight">
                      {p.description}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                  {mode === 'out_of_stock' ? (
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}
                    >
                      0 stock
                    </span>
                  ) : (
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={isZeroStock ? { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' } : { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
                    >
                      {stockVal} stock
                    </span>
                  )}

                  {p.quantity > 1 && (
                    <span className="text-[10px] text-slate-600 font-bold mt-0.5">
                      Qty: {p.quantity}
                    </span>
                  )}
                  {(p.discountPercent > 0 || p.discountPrice > 0) && (
                    <span className="text-[9px] text-emerald-600 font-bold mt-0.5">
                      Discount: {p.discountPrice > 0 ? `${formatCurrency(p.discountPrice)}` : `${p.discountPercent}%`}
                    </span>
                  )}
                  {p.mrp > 0 && (
                    <span className="text-[10px] text-slate-400 font-medium">{formatCurrency(p.mrp)}</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Info: other in-stock products will still be added */}
        {mode === 'out_of_stock' && inStockCount > 0 && (
          <div
            className="px-5 py-2.5 flex items-center gap-2"
            style={{ borderTop: '1px dashed #e2e8f0', background: '#f8fafc' }}
          >
            <CheckCircle2 size={13} color="#16a34a" />
            <span className="text-[11px] text-slate-500">
              <span className="font-bold text-slate-700">{inStockCount}</span>{' '}
              other product{inStockCount !== 1 ? 's' : ''} with available stock will be added regardless.
            </span>
          </div>
        )}

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-5 py-4"
          style={{ borderTop: '1px solid #f1f5f9' }}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 text-[12px] font-semibold transition-all rounded-xl ${activeBtn === 'cancel'
                ? 'bg-slate-200 text-slate-800 ring-2 ring-slate-400 ring-offset-2'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-bold text-white rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] ${activeBtn === 'confirm' ? `ring-2 ${buttonRing} ring-offset-2` : ''
              }`}
            style={{
              background: buttonGradient,
              boxShadow: buttonShadow,
            }}
          >
            {confirmText}
            <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}